import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleInfo,
  faCopy,
  faLightbulb,
  faLock,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

/**
 * Presentational primitives shared by the provider setup guides.
 *
 * Extracted from `AwsS3SetupGuidePage` when the Cloudflare R2 guide was added.
 * Two guides now need byte-identical callouts, tables, figures and code blocks;
 * duplicating them would mean every future fix has to be applied twice and the
 * two pages would drift apart. The AWS guide's rendered output is unchanged —
 * its existing tests cover that.
 *
 * These are deliberately dumb: no data fetching, no routing, no provider
 * knowledge. Each guide supplies its own content and its own figure disclaimer.
 */

export type CalloutTone = 'warning' | 'security' | 'tip' | 'note';

const CALLOUT: Record<
  CalloutTone,
  { label: string; icon: typeof faCircleInfo; wrap: string; chip: string }
> = {
  warning: {
    label: 'WARNING',
    icon: faTriangleExclamation,
    wrap: 'border-destructive/30 bg-destructive/5',
    chip: 'text-destructive',
  },
  security: {
    label: 'SECURITY',
    icon: faLock,
    wrap: 'border-amber-500/30 bg-amber-500/5',
    chip: 'text-amber-600 dark:text-amber-500',
  },
  tip: {
    label: 'TIP',
    icon: faLightbulb,
    wrap: 'border-primary/30 bg-primary/5',
    chip: 'text-primary',
  },
  note: {
    label: 'NOTE',
    icon: faCircleInfo,
    wrap: 'border-border bg-muted/40',
    chip: 'text-muted-foreground',
  },
};

export function Callout({
  tone,
  title,
  children,
}: {
  tone: CalloutTone;
  title?: string;
  children: React.ReactNode;
}) {
  const c = CALLOUT[tone];
  return (
    <div className={cn('my-4 rounded-lg border p-4', c.wrap)}>
      <p className={cn('mb-1.5 flex items-center gap-2 text-xs font-bold tracking-wide', c.chip)}>
        <FontAwesomeIcon icon={c.icon} className="h-3.5 w-3.5" />
        {c.label}
        {title ? <span className="font-semibold normal-case">— {title}</span> : null}
      </p>
      <div className="space-y-2 text-sm leading-relaxed text-foreground">{children}</div>
    </div>
  );
}

export function CodeBlock({ code, label }: { code: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = () => {
    void navigator.clipboard
      ?.writeText(code)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="my-4 overflow-hidden rounded-lg border border-border bg-muted/50">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          {label ?? 'code'}
        </span>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label={`Copy ${label ?? 'code'} to clipboard`}
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="h-3 w-3" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-3 text-xs leading-relaxed text-foreground">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * `disclaimer` is REQUIRED and has no default on purpose. Every figure in these
 * guides is a hand-drawn wireframe, never a captured screenshot, and each guide
 * must say so in its own vendor's terms. A default here would eventually let a
 * page ship an unlabelled — and therefore misleading — illustration.
 */
export function FigureFrame({
  n,
  caption,
  look,
  disclaimer,
  children,
}: {
  n: number;
  caption: string;
  look: string;
  disclaimer: string;
  children: React.ReactNode;
}) {
  return (
    <figure className="my-6 overflow-hidden rounded-lg border border-border">
      <div className="overflow-x-auto bg-muted/30 p-4">{children}</div>
      <figcaption className="border-t border-border bg-background px-4 py-3">
        <p className="text-sm font-semibold text-foreground">
          Figure {n} — {caption}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground">What to look for:</span> {look}
        </p>
        <p className="mt-1 text-[11px] italic text-muted-foreground">{disclaimer}</p>
      </figcaption>
    </figure>
  );
}

export function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm">
        <thead className="bg-muted/60">
          <tr>
            {head.map((h) => (
              <th
                key={h}
                className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="odd:bg-background even:bg-muted/20">
              {r.map((cell, j) => (
                <td
                  key={j}
                  className="border-b border-border px-3 py-2 align-top text-foreground last:border-r-0"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="my-4 space-y-2 rounded-lg border border-border bg-muted/20 p-4">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-2.5 text-sm text-foreground">
          <span
            aria-hidden="true"
            className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded border-2 border-muted-foreground/50"
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

export function Mono({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground">
      {children}
    </code>
  );
}

export function Section({
  id,
  n,
  title,
  children,
}: {
  id: string;
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-border pt-8">
      <h2 className="mb-4 text-xl font-bold text-foreground">
        <span className="mr-2 text-primary">{n}.</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-foreground">{children}</div>
    </section>
  );
}

/**
 * Scroll-spy for a guide's table of contents.
 *
 * Feature-detected: `IntersectionObserver` is absent in jsdom and in older
 * browsers, and the anchors work without it, so the whole page must render when
 * it is unavailable rather than throwing during mount.
 */
export function useTocScrollSpy(ids: readonly string[], initial: string): string {
  const [active, setActive] = useState<string>(initial);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [ids]);

  return active;
}

export function GuideToc({
  items,
  active,
}: {
  items: ReadonlyArray<{ id: string; label: string }>;
  active: string;
}) {
  return (
    <nav aria-label="Table of contents" className="mb-6 lg:mb-0">
      <div className="lg:sticky lg:top-6">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          On this page
        </p>
        <ol className="space-y-0.5 border-l border-border">
          {items.map((t, i) => (
            <li key={t.id}>
              <a
                href={`#${t.id}`}
                aria-current={active === t.id ? 'true' : undefined}
                className={cn(
                  '-ml-px block border-l-2 py-1 pl-3 text-xs transition-colors',
                  active === t.id
                    ? 'border-primary font-semibold text-primary'
                    : 'border-transparent text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground',
                )}
              >
                <span className="mr-1.5 tabular-nums opacity-60">{i + 1}</span>
                {t.label}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
